import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Message, Segment, Table } from 'semantic-ui-react'
import { fetchAdminUsers, patchAdminUser } from 'actions/adminActions'

const STATUS_ORDER = ['pending', 'approved', 'rejected', 'suspended']
const STATUS_OPTIONS = ['PENDING', 'APPROVED', 'REJECTED', 'SUSPENDED']
const ROLE_OPTIONS = ['USER', 'MANAGER', 'SYSTEM_ADMIN']
const MANAGER_ALLOWED_TRANSITIONS = {
  PENDING: ['APPROVED', 'REJECTED'],
  APPROVED: ['SUSPENDED'],
  SUSPENDED: ['APPROVED'],
  REJECTED: [],
}

const toTitle = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/^\w/, (firstCharacter) => firstCharacter.toUpperCase())

const canEditRole = (globalRole) => globalRole === 'SYSTEM_ADMIN'

const getAllowedStatusOptions = ({
  actorRole,
  actorUserId,
  targetUserId,
  targetRole,
  currentStatus,
}) => {
  if (actorRole === 'SYSTEM_ADMIN') {
    // Backend forbids self status updates; keep UI aligned.
    if (actorUserId && actorUserId === targetUserId) {
      return [currentStatus]
    }
    return STATUS_OPTIONS
  }

  if (actorRole !== 'MANAGER') {
    return [currentStatus]
  }

  if (targetRole !== 'USER') {
    return [currentStatus]
  }

  const transitions = MANAGER_ALLOWED_TRANSITIONS[currentStatus] || []
  const options = [currentStatus, ...transitions]
  return Array.from(new Set(options))
}

const AdminPanel = ({ auth, userContext }) => {
  const [usersByStatus, setUsersByStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [savingKey, setSavingKey] = useState('')
  const [idQuery, setIdQuery] = useState('')
  const [cognitoIdQuery, setCognitoIdQuery] = useState('')
  const [emailQuery, setEmailQuery] = useState('')

  const isSystemAdmin = userContext?.globalRole === 'SYSTEM_ADMIN'
  const isStaff =
    userContext?.globalRole === 'MANAGER' ||
    userContext?.globalRole === 'SYSTEM_ADMIN'

  const loadUsers = useCallback(async () => {
    if (!isStaff) {
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await fetchAdminUsers(auth, {})
      setUsersByStatus(data)
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [auth, isStaff])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const handleStatusChange = async (userId, value) => {
    const key = `${userId}:status`
    setSavingKey(key)
    setError('')
    try {
      await patchAdminUser(auth, userId, { user_status: value })
      await loadUsers()
    } catch (patchError) {
      setError(patchError.message || 'Failed to update status')
    } finally {
      setSavingKey('')
    }
  }

  const handleRoleChange = async (userId, value) => {
    const key = `${userId}:role`
    setSavingKey(key)
    setError('')
    try {
      await patchAdminUser(auth, userId, { global_role: value })
      await loadUsers()
    } catch (patchError) {
      setError(patchError.message || 'Failed to update role')
    } finally {
      setSavingKey('')
    }
  }

  const groupedRows = useMemo(() => {
    if (!usersByStatus) {
      return []
    }
    const normalizedIdQuery = idQuery.trim().toLowerCase()
    const normalizedCognitoIdQuery = cognitoIdQuery.trim().toLowerCase()
    const normalizedEmailQuery = emailQuery.trim().toLowerCase()

    const containsQuery = (value, query) => {
      if (!query) {
        return true
      }
      return String(value || '')
        .toLowerCase()
        .includes(query)
    }

    const matchesFilters = (item) => {
      const matchesId = containsQuery(item.id, normalizedIdQuery)
      const matchesCognitoId = containsQuery(item.sub, normalizedCognitoIdQuery)
      const matchesEmail = containsQuery(item.email, normalizedEmailQuery)

      return matchesId && matchesCognitoId && matchesEmail
    }

    return STATUS_ORDER.map((statusKey) => ({
      statusKey,
      section: {
        ...(usersByStatus[statusKey] || {}),
        items: (usersByStatus[statusKey]?.items || []).filter(matchesFilters),
      },
    }))
  }, [usersByStatus, idQuery, cognitoIdQuery, emailQuery])

  const hasActiveFilters =
    idQuery.trim() !== '' ||
    cognitoIdQuery.trim() !== '' ||
    emailQuery.trim() !== ''

  if (!isStaff) {
    return (
      <Segment basic>
        <Message
          warning
          content="Admin access is restricted to managers and system admins."
        />
      </Segment>
    )
  }

  return (
    <Segment basic>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '1rem',
        }}
      >
        <h3 style={{ margin: 0 }}>Admin User Management</h3>
        <Button
          size="small"
          onClick={loadUsers}
          loading={loading}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>

      {error && <Message negative content={error} />}

      <Segment>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.75rem',
          }}
        >
          <div>
            <label htmlFor="admin-search-id">
              <strong>Search by User ID</strong>
            </label>
            <input
              id="admin-search-id"
              style={{ width: '100%' }}
              value={idQuery}
              onChange={(event) => setIdQuery(event.target.value)}
              placeholder="Type user id"
            />
          </div>
          <div>
            <label htmlFor="admin-search-cognito-id">
              <strong>Search by Cognito ID</strong>
            </label>
            <input
              id="admin-search-cognito-id"
              style={{ width: '100%' }}
              value={cognitoIdQuery}
              onChange={(event) => setCognitoIdQuery(event.target.value)}
              placeholder="Type cognito sub"
            />
          </div>
          <div>
            <label htmlFor="admin-search-email">
              <strong>Search by Email</strong>
            </label>
            <input
              id="admin-search-email"
              style={{ width: '100%' }}
              value={emailQuery}
              onChange={(event) => setEmailQuery(event.target.value)}
              placeholder="Type email"
            />
          </div>
        </div>
      </Segment>

      {groupedRows.map(({ statusKey, section }) => {
        const items = section?.items || []
        const totalInGroup = section?.total || 0
        const filteredCount = items.length
        return (
          <Segment key={statusKey}>
            <h4 style={{ marginTop: 0 }}>
              {toTitle(statusKey)} ({totalInGroup})
              {hasActiveFilters &&
                ` - filtered: ${filteredCount}/${totalInGroup}`}
            </h4>
            <Table celled compact size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>User ID</Table.HeaderCell>
                  <Table.HeaderCell>Cognito ID</Table.HeaderCell>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Global Role</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan="5" textAlign="center">
                      No users in this group.
                    </Table.Cell>
                  </Table.Row>
                )}
                {items.map((item) => {
                  const statusBusy = savingKey === `${item.id}:status`
                  const roleBusy = savingKey === `${item.id}:role`
                  const isSelfRow = userContext?.userId === item.id
                  const allowedStatusOptions = getAllowedStatusOptions({
                    actorRole: userContext?.globalRole,
                    actorUserId: userContext?.userId,
                    targetUserId: item.id,
                    targetRole: item.global_role,
                    currentStatus: item.user_status,
                  })
                  const canEditStatus =
                    isSystemAdmin ||
                    (userContext?.globalRole === 'MANAGER' &&
                      item.global_role === 'USER')

                  return (
                    <Table.Row
                      key={item.id}
                      positive={isSelfRow}
                      title={isSelfRow ? 'Your account' : undefined}
                    >
                      <Table.Cell>{item.id}</Table.Cell>
                      <Table.Cell>
                        {item.sub || 'Not available from this API'}
                      </Table.Cell>
                      <Table.Cell>{item.email || '-'}</Table.Cell>
                      <Table.Cell>
                        <select
                          value={item.user_status}
                          disabled={
                            statusBusy ||
                            !canEditStatus ||
                            allowedStatusOptions.length <= 1
                          }
                          title={
                            !canEditStatus
                              ? 'Only system admins can change privileged user statuses.'
                              : undefined
                          }
                          onChange={(event) =>
                            handleStatusChange(item.id, event.target.value)
                          }
                        >
                          {allowedStatusOptions.map((statusOption) => (
                            <option key={statusOption} value={statusOption}>
                              {statusOption}
                            </option>
                          ))}
                        </select>
                      </Table.Cell>
                      <Table.Cell>
                        {canEditRole(userContext?.globalRole) ? (
                          <select
                            value={item.global_role}
                            disabled={roleBusy || !isSystemAdmin || isSelfRow}
                            title={
                              isSelfRow
                                ? 'You cannot change your own global role.'
                                : undefined
                            }
                            onChange={(event) =>
                              handleRoleChange(item.id, event.target.value)
                            }
                          >
                            {ROLE_OPTIONS.map((roleOption) => (
                              <option key={roleOption} value={roleOption}>
                                {roleOption}
                              </option>
                            ))}
                          </select>
                        ) : (
                          item.global_role
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          </Segment>
        )
      })}
    </Segment>
  )
}

AdminPanel.propTypes = {
  auth: PropTypes.object,
  userContext: PropTypes.shape({
    globalRole: PropTypes.string,
    userId: PropTypes.string,
  }),
}

export default AdminPanel
